import React, { useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
// @mui
import {
    Container,
    Typography,
    Stack,
    Paper,
    Box,
    Alert,
    Divider,
} from '@mui/material';
import { ArrowForward, ArrowBack } from '@mui/icons-material';
// components
import FormInput from '../../components/inputs/FormInput';
import OtpInput from '../../components/auth/OtpInput';
import OtpCountdownTimer from '../../components/auth/OtpCountdownTimer';
import OtpResendButton from '../../components/auth/OtpResendButton';
import Btn from '../../components/global/Btn';
import WrapperSection from '../../components/global/WrapperSection';
// hooks & context
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '../../auth/useAuthContext';
import { useEmailUpdate } from '../../hooks/auth/useEmailUpdate';
import { useOtpResend } from '../../hooks/auth/useOtpResend';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { maskEmail } from '../../utils/otpHelpers';

export default function EmailUpdatePage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user, updateUser } = useAuthContext();

    const [step, setStep] = useState(1); // 1: Enter new email, 2: Verify OTP
    const [newEmail, setNewEmail] = useState('');
    const [otp, setOtp] = useState('');

    const emailUpdate = useEmailUpdate();
    const otpResend = useOtpResend();

    // Step 1: Request OTP for new email
    const handleRequestOtp = () => {
        if (!newEmail) {
            toast.error(t('profile.email_required'));
            return;
        }

        if (newEmail === user?.email) {
            toast.error(t('profile.email_different'));
            return;
        }

        emailUpdate.requestUpdate(
            { email: newEmail },
            {
                onSuccess: (data) => {
                    toast.success(data?.message || t('otp.code_sent_success'));
                    setStep(2);
                    setOtp('');
                },
                onError: (error) => {
                    const message = error?.response?.data?.message || t('otp.send_failed');
                    toast.error(message);
                }
            }
        );
    };

    // Step 2: Verify OTP and update email
    const handleVerifyOtp = (otpCode) => {
        emailUpdate.verifyUpdate(
            { email: newEmail, otp: otpCode },
            {
                onSuccess: (data) => {
                    toast.success(data?.message || t('otp.update_success'));

                    // Update user in context
                    if (data?.data && updateUser) {
                        updateUser(data.data);
                    }

                    navigate('/profile');
                },
                onError: (error) => {
                    const message = error?.response?.data?.message || t('otp.invalid_code');
                    toast.error(message);
                }
            }
        );
    };

    const handleResend = () => {
        otpResend.mutate(
            { channel: 'email', identifier: newEmail, purpose: 'verify_email' },
            {
                onSuccess: (data) => {
                    toast.success(data?.message || t('otp.resend_success'));
                    setOtp('');
                },
                onError: (error) => {
                    const message = error?.response?.data?.message || t('otp.resend_failed');
                    toast.error(message);
                }
            }
        );
    };

    const handleExpire = useCallback(() => {
        // Only show expired toast if we are not currently resending
        if (!otpResend.isPending) {
            toast.error(t('otp.code_expired'));
        }
    }, [otpResend.isPending, t]);

    return (
        <>
            <Helmet>
                <title> {t('otp.update_email_title')} | {t('app.name')} </title>
            </Helmet>

            <WrapperSection sx={{ py: 10 }}>
                <Container maxWidth="sm">
                    <Stack spacing={2} sx={{ mb: 5, textAlign: 'center' }}>
                        <Typography variant="h3" sx={{ fontWeight: 700 }}>
                            {t('otp.update_email_title')}
                        </Typography>
                        <Typography color="text.secondary">
                            {t('otp.update_subtitle')}
                        </Typography>
                    </Stack>

                    <Paper elevation={4} sx={{ p: 4, borderRadius: 3 }}>
                        {step === 1 ? (
                            <Box>
                                <Alert severity="info" sx={{ mb: 3 }}>
                                    {t('otp.current_email')}: <strong>{user?.email || t('otp.not_set')}</strong>
                                </Alert>

                                <FormInput
                                    type="email"
                                    label={t('otp.new_email')}
                                    value={newEmail}
                                    onChange={(e) => setNewEmail(e.target.value)}
                                    disabled={emailUpdate.isRequesting}
                                    placeholder="example@example.com"
                                />

                                <Btn
                                    onClick={handleRequestOtp}
                                    width="100%"
                                    widthMob="100%"
                                    sx={{ mt: 3, mx: 'auto' }}
                                    loading={emailUpdate.isRequesting}
                                    disabled={!emailUpdate.isRequesting && !newEmail}
                                    endIcon={<ArrowForward />}
                                >
                                    {t('otp.send_code')}
                                </Btn>
                            </Box>
                        ) : (
                            <Box>
                                <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                                    {t('otp.sent_to')}:<br />
                                    <strong>{maskEmail(newEmail)}</strong>
                                </Alert>

                                <Box sx={{ mb: 3 }}>
                                    <OtpInput
                                        value={otp}
                                        onChange={setOtp}
                                        onComplete={handleVerifyOtp}
                                        disabled={emailUpdate.isVerifying}
                                    />
                                </Box>

                                {emailUpdate.requestData?.data?.expires_at && (
                                    <Box sx={{ mb: 2 }}>
                                        <OtpCountdownTimer
                                            expiresAt={emailUpdate.requestData.data.expires_at}
                                            onExpire={handleExpire}
                                        />
                                    </Box>
                                )}

                                <Btn
                                    onClick={() => handleVerifyOtp(otp)}
                                    width="100%"
                                    widthMob="100%"
                                    sx={{ mb: 2, mx: 'auto' }}
                                    loading={emailUpdate.isVerifying}
                                    disabled={!emailUpdate.isVerifying && otp.length !== 6}
                                >
                                    {t('otp.verify_update')}
                                </Btn>

                                <Divider sx={{ my: 2 }} />

                                <OtpResendButton
                                    onResend={handleResend}
                                    cooldownSeconds={otpResend.cooldownSeconds || emailUpdate.requestData?.data?.cooldown_seconds}
                                    loading={otpResend.isPending}
                                />

                                <Btn
                                    variant="text"
                                    onClick={() => setStep(1)}
                                    width="100%"
                                    widthMob="100%"
                                    startIcon={<ArrowBack />}
                                    sx={{ mt: 2 }}
                                >
                                    {t('common.previous')}
                                </Btn>
                            </Box>
                        )}
                    </Paper>
                </Container>
            </WrapperSection>
        </>
    );
}
